import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { roleService } from '../services/roleService';
import type { CreateRolePayload, Permission, UpdateRoleMetadataPayload } from '../types/role';

const keys = {
  all: ['roles'] as const,
  list: () => [...keys.all, 'list'] as const,
  catalog: () => [...keys.all, 'catalog'] as const,
  detail: (id: string) => [...keys.all, 'detail', id] as const,
};

export function useRolesList() {
  return useQuery({
    queryKey: keys.list(),
    queryFn: roleService.list,
    staleTime: 60_000,
  });
}

export function useRoleCatalog() {
  return useQuery({
    queryKey: keys.catalog(),
    queryFn: roleService.catalog,
    staleTime: 10 * 60_000,
  });
}

export function useRoleDetail(id: string | undefined) {
  return useQuery({
    queryKey: id ? keys.detail(id) : keys.all,
    queryFn: () => roleService.getById(id!),
    enabled: Boolean(id),
  });
}

export function useCreateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateRolePayload) => roleService.create(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.all }),
  });
}

export function useUpdateRoleMetadata(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateRoleMetadataPayload) => roleService.updateMetadata(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.all }),
  });
}

export function useDeleteRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => roleService.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.all }),
  });
}

export function useReplaceRolePermissions(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (permissions: Permission[]) => roleService.replacePermissions(id, permissions),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.detail(id) });
      qc.invalidateQueries({ queryKey: keys.list() });
    },
  });
}

export function useResetRoleDefaults(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => roleService.resetDefaults(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.detail(id) });
      qc.invalidateQueries({ queryKey: keys.list() });
    },
  });
}
